import { useQuery } from "@/lib/convex";
import type { Id } from "@convex/_generated/dataModel";
import { api } from "@convex/_generated/api";
import { useEffectivePersonId } from "../impersonation";

/**
 * What the person you are looking as may do.
 *
 * Follows impersonation, which is the whole point: `viewerProfile` returns
 * BOTH accounts -- `account` is always the real viewer and `effectiveAccount`
 * is whoever is being looked at as -- and every page was reading roles off the
 * real one. So impersonating a student showed the student's data through an
 * administrator's chrome: the catalogue, the assignment workspace and the
 * seminar planner all stayed in the sidebar, and the row menus stayed on the
 * tables. That is not a view of what a student sees, which is the only thing
 * impersonation is for.
 *
 * Five copies of this test had been written out by hand across the pages, with
 * the sidebar's carrying a comment noting that the programme check followed
 * impersonation while the role checks did not. One place to decide it means
 * they cannot drift again.
 *
 * `undefined` while the profile loads, so a caller can tell "not yet" from
 * "not allowed" -- the distinction a page needs to avoid flashing its
 * access-denied screen at somebody who does have access.
 */
export type ViewerAccess = {
  isActivityAdmin: boolean;
  /** Reconciliation and leave: HR's and IT's business rather than the institute's. */
  isDirectoryAdmin: boolean;
  isSysAdmin: boolean;
  /** True when these answers describe somebody other than the signed-in user. */
  isImpersonating: boolean;
};

export function useViewerAccess(): ViewerAccess | undefined {
  const effectivePersonId = useEffectivePersonId();
  const profile = useQuery(api.persons.viewerProfile, {
    effectivePersonId: effectivePersonId as Id<"persons"> | null,
  });

  if (profile === undefined) {
    return undefined;
  }

  const roles = profile !== null
    && profile.status !== "unlinked"
    && profile.status !== "missing_person"
    // The EFFECTIVE account, never `profile.account`: that one is always the
    // real viewer and is what the impersonation control itself reads.
    ? profile.effectiveAccount?.assignedRoles ?? []
    : [];

  return {
    isActivityAdmin: roles.includes("activityAdmin"),
    isDirectoryAdmin: roles.includes("sysAdmin") || roles.includes("hrAdmin"),
    isSysAdmin: roles.includes("sysAdmin"),
    isImpersonating: profile?.impersonation?.active ?? false,
  };
}
